import * as THREE from "three";

/**
 * Merges vertices by position only (ignores normals, UVs).
 * Produces a minimal geometry suitable for EdgesGeometry - avoids spurious
 * edges from duplicate vertices at the same position with different attributes.
 *
 * @param {THREE.BufferGeometry} geometry - Source geometry
 * @param {number} tolerance - Position merge tolerance (default 1e-4)
 * @returns {THREE.BufferGeometry} New geometry with welded positions
 */
export function mergeVerticesByPosition(geometry, tolerance = 1e-4) {
  const positionAttr = geometry.getAttribute("position");
  const indexAttr = geometry.getIndex();
  const vertexCount = indexAttr ? indexAttr.count : positionAttr.count;

  const hashToIndex = new Map();
  const newPositions = [];
  const newIndices = [];

  const precision = 1 / tolerance;
  const getKey = (i) => {
    const idx = indexAttr ? indexAttr.getX(i) : i;
    const x = Math.round(positionAttr.getX(idx) * precision);
    const y = Math.round(positionAttr.getY(idx) * precision);
    const z = Math.round(positionAttr.getZ(idx) * precision);
    return `${x},${y},${z}`;
  };

  for (let i = 0; i < vertexCount; i++) {
    const idx = indexAttr ? indexAttr.getX(i) : i;
    const key = getKey(i);

    if (hashToIndex.has(key)) {
      newIndices.push(hashToIndex.get(key));
    } else {
      const newIdx = newPositions.length / 3;
      hashToIndex.set(key, newIdx);
      newPositions.push(
        positionAttr.getX(idx),
        positionAttr.getY(idx),
        positionAttr.getZ(idx)
      );
      newIndices.push(newIdx);
    }
  }

  const result = new THREE.BufferGeometry();
  result.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(newPositions, 3)
  );
  result.setIndex(newIndices);
  return result;
}
